import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Volume2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "@/lib/api";

export default function NotificationBell() {
  const [data, setData] = useState({ unseen_count: 0, items: [] });
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef(null);
  const openRef = useRef(false);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem("bd_alert_sound") !== "off");

  const playChime = useCallback(() => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const now = ctx.currentTime;
      [880, 1174.66].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.value = freq;
        const start = now + i * 0.14;
        gain.gain.setValueAtTime(0.0001, start);
        gain.gain.exponentialRampToValueAtTime(0.12, start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.5);
        osc.connect(gain); gain.connect(ctx.destination);
        osc.start(start); osc.stop(start + 0.55);
      });
      setTimeout(() => ctx.close(), 1200);
    } catch {}
  }, []);

  const load = useCallback(() => {
    api.get("/admin/notifications").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  // Real-time push via SSE (instant), with a slow poll fallback.
  useEffect(() => {
    const es = new EventSource(`${API}/admin/stream`, { withCredentials: true });
    es.onmessage = (e) => {
      try {
        const evt = JSON.parse(e.data);
        if (evt.type === "enquiry") {
          setData((d) => ({
            unseen_count: openRef.current ? d.unseen_count : d.unseen_count + 1,
            items: [{ id: evt.id, name: evt.name, category: evt.category, created_at: evt.created_at, status: "New", is_new: true }, ...d.items].slice(0, 15),
          }));
          toast(`New ${evt.category} enquiry`, { description: evt.name });
          if (localStorage.getItem("bd_alert_sound") !== "off") playChime();
          if ("Notification" in window && Notification.permission === "granted") {
            try { new Notification("New enquiry — Black Diamond", { body: `${evt.name} · ${evt.category}` }); } catch {}
          }
        }
      } catch {}
    };
    const poll = setInterval(load, 60000);
    return () => { es.close(); clearInterval(poll); };
  }, [load, playChime]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", onClick);
    return () => window.removeEventListener("mousedown", onClick);
  }, []);

  const toggle = async () => {
    const next = !open;
    setOpen(next);
    openRef.current = next;
    if (next && "Notification" in window && Notification.permission === "default") {
      try { await Notification.requestPermission(); } catch {}
    }
    if (next && data.unseen_count > 0) {
      await api.post("/admin/notifications/seen").catch(() => {});
      setData((d) => ({ ...d, unseen_count: 0 }));
    }
  };

  const toggleSound = () => {
    setSoundOn((v) => {
      const nv = !v;
      localStorage.setItem("bd_alert_sound", nv ? "on" : "off");
      return nv;
    });
  };

  const go = () => { setOpen(false); openRef.current = false; navigate("/admin/enquiries"); };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell" onClick={toggle} className="relative p-2 text-bd-ivory/60 hover:text-bd-gold transition-colors">
        <Bell size={20} strokeWidth={1.5} />
        {data.unseen_count > 0 && (
          <span data-testid="notification-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-bd-gold text-bd-black text-[0.6rem] font-bold flex items-center justify-center">
            {data.unseen_count > 9 ? "9+" : data.unseen_count}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="notification-panel" className="absolute right-0 mt-2 w-80 bg-bd-charcoal border border-white/10 shadow-2xl z-50">
          <div className="px-4 py-3 border-b border-white/10 flex items-start justify-between gap-2">
            <div>
              <p className="font-serif text-lg text-bd-ivory">Enquiries</p>
              <p className="text-xs text-bd-ivory/40">Live · latest website submissions</p>
            </div>
            <button data-testid="alert-sound-toggle" onClick={toggleSound} title={soundOn ? "Mute alert sound" : "Unmute alert sound"} className="p-1.5 text-bd-ivory/50 hover:text-bd-gold transition-colors">
              {soundOn ? <Volume2 size={16} /> : <VolumeX size={16} />}
            </button>
          </div>
          <div className="max-h-96 overflow-y-auto divide-y divide-white/5">
            {data.items.length === 0 ? (
              <p className="px-4 py-8 text-center text-sm text-bd-ivory/30">No enquiries yet.</p>
            ) : (
              data.items.map((n) => (
                <button key={n.id} onClick={go} className="w-full text-left px-4 py-3 hover:bg-white/[0.03] transition-colors flex items-start gap-3">
                  {n.is_new && <span className="w-1.5 h-1.5 rounded-full bg-bd-gold mt-1.5 flex-shrink-0" />}
                  <span className={`flex-1 min-w-0 ${n.is_new ? "" : "pl-[18px]"}`}>
                    <span className="block text-sm text-bd-ivory truncate">{n.name}</span>
                    <span className="block text-xs text-bd-ivory/40">{n.category} · {new Date(n.created_at).toLocaleDateString("en-GB")}</span>
                  </span>
                </button>
              ))
            )}
          </div>
          <button onClick={go} className="w-full px-4 py-3 text-xs tracking-[0.15em] uppercase text-bd-gold hover:bg-white/[0.03] border-t border-white/10">
            View all enquiries
          </button>
        </div>
      )}
    </div>
  );
}
